import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Accordion parents open by default (and after each login): only the Orders group. */
export const DEFAULT_EXPANDED_ITEMS: string[] = ['orders'];

interface SidebarState {
	collapsed: boolean;
	expandedItems: string[]; // ids of expanded parent items
	toggleCollapsed: () => void;
	toggleExpanded: (id: string) => void;
	resetExpanded: () => void;
}

/**
 * Sidebar chrome state. `collapsed` drives `--sidebar-width` (220px / 64px) and
 * `expandedItems` tracks which accordion parents are open. Persisted to
 * localStorage so the layout survives reloads and route changes (the store is
 * read at the AppShell level, not inside the unmounting sidebar component).
 * Defaults to (and is reset on login to) `DEFAULT_EXPANDED_ITEMS` — Orders only.
 */
export const useSidebarStore = create<SidebarState>()(
	persist(
		(set, get) => ({
			collapsed: false,
			expandedItems: [...DEFAULT_EXPANDED_ITEMS],
			toggleCollapsed: () => set({ collapsed: !get().collapsed }),
			toggleExpanded: id => {
				const current = get().expandedItems;
				set({
					expandedItems: current.includes(id) ? current.filter(x => x !== id) : [...current, id]
				});
			},
			resetExpanded: () => set({ expandedItems: [...DEFAULT_EXPANDED_ITEMS] })
		}),
		{ name: 'admin-v2-sidebar' }
	)
);
